import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const STEPS = { PHONE: "phone", CODE: "code", NAME: "name" };

export default function Login() {
  const { sendPhoneOtp, verifyPhoneOtp } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState(STEPS.PHONE);
  const [phone, setPhone] = useState("");
  const [normalizedPhone, setNormalizedPhone] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const codeRefs = useRef([]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleSendOtp = async (e) => {
    e?.preventDefault();
    if (!/^5\d{8}$/.test(phone)) {
      toast.error("رقم الجوال يجب أن يبدأ بـ 5 ويتكون من 9 أرقام");
      return;
    }
    setLoading(true);
    try {
      const res = await sendPhoneOtp("+966" + phone);
      setNormalizedPhone(res.phone);
      setStep(STEPS.CODE);
      setResendIn(30);
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
      toast.success("تم إرسال رمز التحقق");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل إرسال الرمز");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...code];
    next[idx] = val;
    setCode(next);
    if (val && idx < 5) codeRefs.current[idx + 1]?.focus();
    if (val && idx === 5 && next.every((d) => d)) {
      submitCode(next.join(""));
    }
  };

  const handleCodeKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
  };

  const submitCode = async (joinedCode, withName) => {
    setLoading(true);
    try {
      const res = await verifyPhoneOtp(normalizedPhone, joinedCode, withName);
      if (res.needName) {
        setStep(STEPS.NAME);
        return;
      }
      toast.success("مرحباً بك");
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "رمز غير صحيح");
      setCode(["", "", "", "", "", ""]);
      setTimeout(() => codeRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("يرجى إدخال اسمك");
      return;
    }
    submitCode(code.join(""), name.trim());
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-start sm:items-center justify-center px-5 py-8 sm:py-14 bg-[#FAFAFA]">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-[#E05D3A] mb-4 shadow-lg shadow-[#E05D3A]/30">
            <span className="font-display text-white text-3xl font-black">جايك</span>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-black text-[#1A1A1A]">
            {step === STEPS.PHONE && "أدخل رقم جوالك"}
            {step === STEPS.CODE && "تحقق من رقم جوالك"}
            {step === STEPS.NAME && "أكمل ملفك الشخصي"}
          </h1>
          <p className="text-[#6B6B6B] mt-2 text-sm">
            {step === STEPS.PHONE && "سنرسل لك رمز تحقق عبر رسالة نصية"}
            {step === STEPS.CODE && (
              <>تم إرسال الرمز إلى <span dir="ltr" className="font-semibold">{normalizedPhone}</span></>
            )}
            {step === STEPS.NAME && "خطوة أخيرة، عرّفنا على اسمك"}
          </p>
        </div>

        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-sm border border-[#EFEFEF]">
          {step === STEPS.PHONE && (
            <form onSubmit={handleSendOtp} className="space-y-5">
              <div>
                <Label className="text-[#1A1A1A] text-sm font-medium">رقم الجوال</Label>
                <div className="mt-2 flex items-stretch rounded-2xl border-2 border-[#EAEAEA] focus-within:border-[#E05D3A] transition-colors overflow-hidden bg-white" dir="ltr">
                  <div className="flex items-center px-3 bg-[#F7F7F7] border-r border-[#EAEAEA] text-[#1A1A1A] font-semibold text-sm gap-1.5">
                    <span className="text-base">🇸🇦</span>
                    <span>+966</span>
                  </div>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={9}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 9))}
                    placeholder="5XXXXXXXX"
                    className="flex-1 px-4 py-4 text-base font-medium tracking-wide outline-none bg-transparent"
                    required
                    autoFocus
                    data-testid="phone-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading || phone.length !== 9}
                className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6 text-base font-bold disabled:opacity-50"
                data-testid="phone-continue-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "المتابعة"}
              </Button>
              <p className="text-xs text-center text-[#8B8B8B] leading-relaxed pt-2">
                بمتابعتك، فإنك توافق على{" "}
                <Link to="/terms" className="text-[#E05D3A] font-semibold underline">شروط الاستخدام</Link>
                {" "}و{" "}
                <Link to="/privacy" className="text-[#E05D3A] font-semibold underline">سياسة الخصوصية</Link>
              </p>
            </form>
          )}

          {step === STEPS.CODE && (
            <div className="space-y-5">
              <div className="flex justify-center gap-2 sm:gap-3" dir="ltr">
                {code.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (codeRefs.current[i] = el)}
                    type="tel"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={(e) => handleCodeChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    className="w-11 h-14 sm:w-12 sm:h-16 text-2xl font-bold text-center rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] focus:outline-none bg-white"
                    data-testid={`otp-digit-${i}`}
                  />
                ))}
              </div>
              <Button
                onClick={() => submitCode(code.join(""))}
                disabled={loading || code.some((d) => !d)}
                className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6 text-base font-bold disabled:opacity-50"
                data-testid="verify-otp-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "تحقق"}
              </Button>
              <div className="flex items-center justify-between text-sm pt-1">
                <button
                  type="button"
                  onClick={() => { setStep(STEPS.PHONE); setCode(["","","","","",""]); }}
                  className="text-[#6B6B6B] hover:text-[#1A1A1A] font-medium"
                >
                  ← تعديل الرقم
                </button>
                {resendIn > 0 ? (
                  <span className="text-[#8B8B8B]">إعادة الإرسال خلال {resendIn}s</span>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="text-[#E05D3A] font-semibold hover:underline"
                  >
                    إعادة إرسال الرمز
                  </button>
                )}
              </div>
            </div>
          )}

          {step === STEPS.NAME && (
            <form onSubmit={handleNameSubmit} className="space-y-5">
              <div>
                <Label className="text-[#1A1A1A] text-sm font-medium">الاسم الكامل</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="مثال: محمد العتيبي"
                  className="mt-2 rounded-2xl border-2 border-[#EAEAEA] focus:border-[#E05D3A] py-6 text-base"
                  required
                  autoFocus
                  data-testid="name-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !name.trim()}
                className="w-full rounded-2xl bg-[#E05D3A] hover:bg-[#C24B2C] text-white py-6 text-base font-bold disabled:opacity-50"
                data-testid="name-continue-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "ابدأ الطلب"}
              </Button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link
            to="/staff-login"
            className="text-xs text-[#8B8B8B] hover:text-[#1A1A1A] underline"
            data-testid="staff-login-link"
          >
            دخول الموظفين (إدارة، سائق، صاحب مطعم)
          </Link>
        </div>
      </div>
    </div>
  );
}
